import { Fragment, createElement, isValidElement } from "react"
import { ALLOWED_ELEMENT_ATTRS, ALLOWED_ELEMENT_TAGS, TAG_ALIASES } from "./localization-contract"
import { normalizeFrequencyPeriod, isPreserveCaseToken } from "./case-normalization"
import { formatDateValue, formatNumberValue, getPluralRules, toPluralNumber } from "./localized-formatters"

/**
 * @typedef {object} InterpretOptions
 * @property {(name: string) => string} [onMissingValue]
 */

/**
 * @param {string} href
 * @returns {boolean}
 */
export function isSafeHref(href) {
    if (href == null || href === "") return false
    const s = String(href).trim()
    if (s.startsWith("/") || s.startsWith("#") || s.startsWith("?")) return true
    try {
        const u = new URL(s, "https://example.invalid")
        const p = u.protocol
        return p === "http:" || p === "https:" || p === "mailto:" || p === "tel:"
    } catch {
        return false
    }
}

/**
 * Substitute `{name}` in a string from values; used for element attrs.
 * @param {string} s
 * @param {Record<string, unknown>} values
 * @returns {string}
 */
export function substituteAttrString(s, values) {
    if (typeof s !== "string") return ""
    return s.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, name) => {
        const v = values[name]
        if (v == null) return ""
        if (typeof v === "object" && v !== null && "value" in v) return String(v.value ?? "")
        return String(v)
    })
}

/**
 * @param {unknown} v
 * @param {number} count
 * @returns {string}
 */
function englishPluralizeDisplay(v, count) {
    const s = String(v ?? "")
    const proto = String.prototype
    if (typeof proto.pluralize === "function") {
        return proto.pluralize.call(s, count)
    }
    return s
}

/**
 * @param {string} template
 * @param {Record<string, unknown>} values
 * @param {string} locale
 * @param {{ vocabPlural?: Record<string, { count: string }> }} [meta]
 * @returns {string}
 */
export function interpretTemplateFallback(template, values, locale, meta) {
    if (!template) return ""
    const vocabPlural = meta?.vocabPlural ?? {}
    let out = ""
    let i = 0
    while (i < template.length) {
        if (template[i] === "{" && template[i + 1] === "{") {
            out += "{"
            i += 2
            continue
        }
        if (template[i] === "}" && template[i + 1] === "}") {
            out += "}"
            i += 2
            continue
        }
        if (template[i] === "{") {
            const end = template.indexOf("}", i + 1)
            if (end === -1) {
                out += template.slice(i)
                break
            }
            const name = template.slice(i + 1, end).trim()
            i = end + 1
            if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
                out += `{${name}}`
                continue
            }
            const raw = values[name]
            const spec = vocabPlural[name]
            if (spec?.count) {
                const c = toPluralNumber(values[spec.count])
                const canon = isPreserveCaseToken(raw) ? String(raw) : normalizeFrequencyPeriod(String(raw))
                out += englishPluralizeDisplay(canon || raw, c)
                continue
            }
            if (raw instanceof Date || (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw))) {
                out += formatDateValue(locale, raw)
                continue
            }
            if (typeof raw === "number" || (typeof raw === "string" && raw !== "" && !Number.isNaN(Number(raw)))) {
                out += formatNumberValue(locale, raw)
                continue
            }
            if (raw != null && typeof raw === "object" && "value" in raw) {
                out += String(raw.value ?? "")
                continue
            }
            out += raw == null ? "" : String(raw)
            continue
        }
        out += template[i]
        i++
    }
    return out
}

/**
 * @param {unknown} node
 * @param {Record<string, unknown>} values
 * @param {string} locale
 * @param {InterpretOptions} [options]
 * @returns {string|import("react").ReactNode}
 */
function interpretNode(node, values, locale, options) {
    if (!node || typeof node !== "object") return ""
    const { t } = node
    const onMissing = options?.onMissingValue ?? ((n) => `{${n}}`)

    switch (t) {
        case "text":
            return node.v == null ? "" : String(node.v)
        case "value": {
            const { n } = node
            const f = node.f || "text"
            const v = values[n]
            if (v === undefined || v === null) return onMissing(n)
            if (f === "number") return formatNumberValue(locale, v, node.opts)
            if (f === "date") return formatDateValue(locale, v, node.opts)
            if (f === "duration") return formatNumberValue(locale, v, node.opts)
            if (typeof v === "object" && v !== null && "value" in v) return String(v.value ?? "")
            return String(v)
        }
        case "plural": {
            const { n } = node
            const forms = node.forms || {}
            const num = toPluralNumber(values[n])
            const cat = getPluralRules(locale).select(num)
            return forms[cat] ?? forms.other ?? ""
        }
        case "vocabPlural": {
            const wordKey = node.word
            const countKey = node.count
            const vocab = node.vocab || {}
            const raw = values[wordKey]
            const key = isPreserveCaseToken(raw) ? String(raw) : normalizeFrequencyPeriod(String(raw))
            const num = toPluralNumber(values[countKey])
            const cat = getPluralRules(locale).select(num)
            const entry = vocab[key] || vocab[String(key)]
            if (!entry) return String(raw ?? "")
            if (typeof entry === "string") return entry
            return entry[cat] ?? entry.other ?? ""
        }
        case "select": {
            const { n } = node
            const cases = node.cases || {}
            const v = values[n]
            const key = v == null ? "" : String(v)
            if (Object.prototype.hasOwnProperty.call(cases, key)) return cases[key]
            return node.fallback != null ? String(node.fallback) : ""
        }
        case "element": {
            const rawTag = node.tag
            if (!ALLOWED_ELEMENT_TAGS.includes(rawTag)) return ""
            const tag = TAG_ALIASES[rawTag] || rawTag
            const allowed = ALLOWED_ELEMENT_ATTRS[rawTag] || []
            const attrs = {}
            if (node.attrs && typeof node.attrs === "object") {
                for (const [k, val] of Object.entries(node.attrs)) {
                    if (!allowed.includes(k)) continue
                    let s = substituteAttrString(String(val ?? ""), values)
                    if (k === "href" && !isSafeHref(s)) s = "#"
                    attrs[k] = s
                }
            }
            const kids = (node.children || []).map((c, idx) => {
                const r = interpretNode(c, values, locale, options)
                return isValidElement(r) || (typeof r === "string" && r !== "")
                    ? createElement(Fragment, { key: idx }, r)
                    : null
            })
            return createElement(tag, attrs, ...kids.filter(Boolean))
        }
        default:
            return ""
    }
}

/**
 * @param {unknown[]} nodes
 * @param {Record<string, unknown>} values
 * @param {string} locale
 * @param {InterpretOptions} [options]
 * @returns {string|import("react").ReactNode}
 */
export function interpretProgram(nodes, values, locale, options) {
    if (!Array.isArray(nodes) || !nodes.length) return ""
    const parts = nodes.map((n) => interpretNode(n, values, locale, options))
    const hasElement = parts.some((p) => isValidElement(p))
    if (!hasElement) return parts.map((p) => (p == null ? "" : String(p))).join("")
    return createElement(Fragment, null, ...parts.map((p, i) => createElement(Fragment, { key: i }, p)))
}

/**
 * @param {unknown} program
 * @param {string} template
 * @param {Record<string, unknown>} values
 * @param {string} locale
 * @param {{ vocabPlural?: Record<string, { count: string }> }} [fallbackMeta]
 * @param {InterpretOptions} [options]
 * @returns {string|import("react").ReactNode}
 */
export function interpretMessage(program, template, values, locale, fallbackMeta, options) {
    if (program && typeof program === "object" && Array.isArray(program.nodes)) {
        return interpretProgram(program.nodes, values, locale, options)
    }
    return interpretTemplateFallback(template, values, locale, fallbackMeta)
}
